// The autopilot trip's own life, as the two events that bracket it: one
// trip_start when GO takes the plan, and one trip_end at the line that ends it,
// whichever way it ends and however many times a caller says so.

import type { EventName, EventParams } from '../visits/events.ts';
import { roundTo } from '../visits/params.ts';
import type { AutopilotCommand } from '../sim/autopilot';
import type { TransferPlan } from '../sim/transfer';

/** The one call a trip reports through: `track` from src/visits/bus.ts,
 *  which is a no-op with no consent installed, or a fake in a test. */
export type TripTracker = <N extends EventName>(name: N, params: EventParams[N]) => void;

/** What GO took, and the body the trip leaves. */
export interface TripStart {
  readonly plan: Pick<TransferPlan, 'destinationId' | 'totalDvKmS' | 'timeOfFlightDays'>;
  readonly originId: string;
}

export interface TripLogConfig {
  readonly track: TripTracker;
  /** The one clock's own seconds, `gameClock().t`; only the difference across
   *  the trip is read. */
  readonly simSeconds: () => number;
  /** A monotonic real millisecond clock, `performance.now()`. */
  readonly realMs: () => number;
}

export interface TripLog {
  /** GO took the plan: one trip_start. A trip already open is the one flying,
   *  and a second start would owe one trip_end to two of them. */
  begin(start: TripStart): void;
  /** The autopilot's own command, each time it gives one: a done one is the
   *  trip's end, reported in the phase it ended in. Nothing else is read. */
  note(cmd: Pick<AutopilotCommand, 'done' | 'ended'> | null): void;
  /** A way out with no command to read it off: the CANCEL button, and the
   *  scene tear-down behind it. */
  cancel(): void;
  /** One WARP TO NEXT the trip ran, which its end reports as a count. */
  skipped(): void;
  /** True while a trip is open. */
  readonly open: boolean;
}

export function createTripLog(config: TripLogConfig): TripLog {
  /** Null while no trip is open, which is the whole guard: every way out calls
   *  `end`, and only the first of them finds a trip to report. */
  let trip: { readonly start: TripStart; readonly atSimS: number; readonly atMs: number; skips: number } | null = null;

  function end(outcome: EventParams['trip_end']['outcome']): void {
    const t = trip;
    if (t === null) return;
    trip = null;
    config.track('trip_end', {
      target_id: t.start.plan.destinationId,
      outcome,
      // A clock rewound behind the start reads as no time flown, not as negative days.
      sim_days: roundTo(Math.max(0, config.simSeconds() - t.atSimS) / 86400, 1),
      real_s: roundTo((config.realMs() - t.atMs) / 1000, 1),
      skips: t.skips,
    });
  }

  return {
    begin(start: TripStart): void {
      if (trip !== null) return;
      trip = { start, atSimS: config.simSeconds(), atMs: config.realMs(), skips: 0 };
      // The plan's own figures, rounded the way `trip_planned` rounds the same
      // crossing, so the two events agree about one trip.
      config.track('trip_start', {
        origin_id: start.originId,
        target_id: start.plan.destinationId,
        dv_kms: roundTo(start.plan.totalDvKmS, 3),
        tof_days: roundTo(start.plan.timeOfFlightDays, 1),
      });
    },
    note(cmd: Pick<AutopilotCommand, 'done' | 'ended'> | null): void {
      if (cmd === null || !cmd.done || cmd.ended === null) return;
      end(cmd.ended.phase);
    },
    cancel(): void { end('cancelled'); },
    skipped(): void { if (trip !== null) trip.skips += 1; },
    get open(): boolean { return trip !== null; },
  };
}
